import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { RANGE_OPTIONS, type RangeKey } from '../../../hooks/useBillingRange';
import type { HistoryArchiveStatus } from '../../../services/dmMonitoringOverviewHistory';
import { InfoHint } from '../../billing/overview/OverviewCard';
import { RangeControl } from '../../billing/overview/RangeControl';

export const ARCHIVE_NOT_CONNECTED =
    "Older history isn't connected in this environment; ranges count only what's in the database.";
const ARCHIVE_FAILED = "Older history couldn't be loaded, showing recent data only.";

export function ArchiveFailedNote() {
    return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
            <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {ARCHIVE_FAILED}
        </span>
    );
}

interface OverviewRangeBarProps {
    range: RangeKey;
    onRangeChange: (next: RangeKey) => void;
    rangeLabel: string;
    // Null until a range has loaded.
    archive: HistoryArchiveStatus | null;
    switching: boolean;
    failed: boolean;
    onRetry: () => void;
}

export function OverviewRangeBar({
    range,
    onRangeChange,
    rangeLabel,
    archive,
    switching,
    failed,
    onRetry
}: OverviewRangeBarProps) {
    return (
        <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 pl-1 text-sm text-gray-500">
                <span className="font-semibold text-gray-700">DM history</span>
                <span className="text-gray-300">·</span>
                <span>{rangeLabel}</span>
                {archive === 'not_configured' && <InfoHint text={ARCHIVE_NOT_CONNECTED} />}
                {switching && <span className="text-xs text-gray-400">Loading…</span>}
                {failed && (
                    <button
                        type="button"
                        onClick={onRetry}
                        className="text-xs font-medium text-red-500 hover:text-red-600"
                    >
                        Couldn&apos;t load this range, retry
                    </button>
                )}
            </div>
            <div className="flex items-center gap-3">
                {archive === 'failed' && <ArchiveFailedNote />}
                <RangeControl active={range} onChange={onRangeChange} options={RANGE_OPTIONS} />
            </div>
        </div>
    );
}
